import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { ConfigurationCard } from "features/settings/common/styles";
import Link from "common/Typography/Link";
import Button from "common/Buttons/Button";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  CANCEL_SUBSCRIPTION_PATH,
  IS_WEB_VIEW,
} from "common/constants";
import useMedia from "utils/hooks/useMedia";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import CancelSubscriptionModal from "features/settings/Billing/CancelSubscriptionModal";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { useRestrictedCountriesPurchase } from "utils/hooks/misc";
import { getPurchaseSubscriptionPath } from "features/settings/common/utils";
import usePlanInfo from "@shared/features/settings/subscription/hooks/usePlanInfo";
import { openMessage } from "utils/misc.tsx";
import { useHistory } from "react-router-dom";
import {
  useCancelAutoRenewalWarning,
  useHideBillingButtons,
  useUpgradePlanPath,
} from "features/settings/common/hooks.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import styled, { css } from "styled-components";
import { Sparkles } from "lucide-react";
import { NEW_LOOK_ICON_STROKE, Surface } from "common/NewLook";
import {
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import { getUserExpirationInfo } from "@shared/utils/misc";

const PlanInfo = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const {
    user,
    theme,
    isFetchingUser,
    getExpirationText,
    isTrialHelperData,
    cancelSubscriptionModalProps,
    isFree,
    isLoading,
    extendPlanError,
  } = usePlanInfo({ isWebview: IS_WEB_VIEW });
  const history = useHistory();
  const upgradePath = useUpgradePlanPath();

  const isMobile = useMedia([`(max-width: 650px)`], [true], false);
  const minifyPurchaseButton = useMedia([`(max-width: 450px)`], [true], false);
  const { onPurchaseClick } = useRestrictedCountriesPurchase();
  const cancelAutoRenewalWarning = useCancelAutoRenewalWarning();
  const hideActionsLinks = useHideBillingButtons();

  const onViewPlansClick = () => {
    onPurchaseClick(getPurchaseSubscriptionPath({ isAllPlans: true }));
  };

  const onPurchaseSubscriptionClick = () => {
    if (extendPlanError) {
      openMessage(extendPlanError, "warning");
    } else {
      onPurchaseClick(BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH);
    }
  };

  const getActionLink = () => {
    const isNewLook = theme.isNewLook;
    if (user?.has_auto_renewal) {
      return (
        <StyledLink
          $quiet={isNewLook}
          onClick={() => {
            if (cancelAutoRenewalWarning) {
              openMessage(cancelAutoRenewalWarning, "warning");
            } else {
              history.push(CANCEL_SUBSCRIPTION_PATH);
            }
          }}
        >
          {t("plan_info.cancel_auto_renewal")}
        </StyledLink>
      );
    } else if (user?.isPurchaseSubscription() && !isFree) {
      const button = (
        <Button type="primary" onClick={onPurchaseSubscriptionClick}>
          {t("plan_info.purchase")}
          {minifyPurchaseButton ? "" : ` ${t("plan_info.subscription")}`}
        </Button>
      );
      if (user?.is_trial) {
        return <HelperTooltip {...isTrialHelperData} triggerElement={button} />;
      } else {
        return button;
      }
    } else {
      return (
        <Flex style={{ columnGap: isNewLook ? 8 : 20 }}>
          {!isFree &&
            (isNewLook ? (
              <Button onClick={onPurchaseSubscriptionClick}>
                {t("plan_info.extend")}
              </Button>
            ) : (
              <StyledLink onClick={onPurchaseSubscriptionClick}>
                {t("plan_info.extend")}
              </StyledLink>
            ))}
          {!user?.isMaxSubscription() &&
            !IS_WEB_VIEW &&
            (isNewLook ? (
              <Button
                type="primary"
                onClick={() =>
                  isFree
                    ? onPurchaseSubscriptionClick()
                    : onPurchaseClick(upgradePath)
                }
              >
                <Sparkles size={15} strokeWidth={NEW_LOOK_ICON_STROKE} />
                {t("plan_info.upgrade")}
              </Button>
            ) : (
              <StyledLink
                onClick={() =>
                  isFree
                    ? onPurchaseSubscriptionClick()
                    : onPurchaseClick(upgradePath)
                }
              >
                {t("plan_info.upgrade")}
              </StyledLink>
            ))}
        </Flex>
      );
    }
  };

  const getPackageDisplay = () => {
    const packageComponent = (
      <Text
        as="div"
        style={{
          paddingRight: 16,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {user?.getPackageDisplay()}
      </Text>
    );
    if (isMobile) {
      return (
        <Text as="div">
          {packageComponent}
          {!isFree && expiration}
        </Text>
      );
    }
    return packageComponent;
  };

  const expiration = (
    <Text
      as="div"
      type={user?.isExpired() ? "danger" : "primary"}
      style={{ paddingRight: 20, display: "flex", flexWrap: "wrap" }}
    >
      <div>{getExpirationText()}</div>
    </Text>
  );

  if (theme.isNewLook) {
    const [expirationAction, formattedExpiration] = getUserExpirationInfo(
      user,
      t
    );
    return (
      <HeroSurface $padding="24px">
        <LoadingSpinnerOverlay
          isLoading={isFetchingUser || isLoading}
          backgroundColor={theme.colors.cardBgColor}
        >
          <NewLookPlanRow>
            <NewLookPlanTexts>
              <NewLookPlanLabel>
                {isMobile
                  ? t("plan_info.current_plan")
                  : t("plan_info.my_plan")}
              </NewLookPlanLabel>
              <NewLookPlanName>{user?.getPackageDisplay()}</NewLookPlanName>
              {!isFree && formattedExpiration && (
                <NewLookPlanMeta $expired={!!user?.isExpired()}>
                  {`${expirationAction} on ${formattedExpiration}`}
                </NewLookPlanMeta>
              )}
            </NewLookPlanTexts>
            {!hideActionsLinks && (
              <NewLookPlanActions>
                <Button onClick={() => onViewPlansClick()}>
                  {t("plan_info.view_plans")}
                </Button>
                {getActionLink()}
              </NewLookPlanActions>
            )}
          </NewLookPlanRow>
        </LoadingSpinnerOverlay>
        <CancelSubscriptionModal {...cancelSubscriptionModalProps} />
      </HeroSurface>
    );
  }

  return (
    <ConfigurationCard style={{ marginTop: 16 }}>
      <LoadingSpinnerOverlay
        isLoading={isFetchingUser || isLoading}
        backgroundColor={theme.colors.cardBgColor}
      >
        <Flex style={{ marginBottom: 12 }}>
          <Title level={3}>
            {isMobile ? t("plan_info.current_plan") : t("plan_info.my_plan")}
          </Title>
          {!hideActionsLinks && (
            <Link onClick={() => onViewPlansClick()}>
              {t("plan_info.view_plans")}
            </Link>
          )}
        </Flex>
        <Flex $justifyContent="space-between">
          {getPackageDisplay()}
          {!isFree && !isMobile && expiration}
          {!hideActionsLinks && getActionLink()}
        </Flex>
      </LoadingSpinnerOverlay>
      <CancelSubscriptionModal {...cancelSubscriptionModalProps} />
    </ConfigurationCard>
  );
};

export default PlanInfo;

const StyledLink = styled(Link)<{ $quiet?: boolean }>`
  text-align: end;
  ${(p) =>
    p.$quiet &&
    css`
      &&& {
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary} !important;
      }
      &&&:hover {
        color: ${getNewLookTokens(p.theme).down} !important;
      }
    `}
`;
const HeroSurface = styled(Surface)`
`;
const NewLookPlanRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;
const NewLookPlanTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 200px;
  min-width: 0;
`;
const NewLookPlanLabel = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const NewLookPlanName = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookPlanMeta = styled.div<{ $expired: boolean }>`
  ${newLookType.meta};
  color: ${(p) =>
    p.$expired
      ? getNewLookTokens(p.theme).down
      : getNewLookTokens(p.theme).muted};
`;
const NewLookPlanActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px 16px;
  flex-wrap: wrap;
  @media (max-width: 650px) {
    width: 100%;
  }
`;
